import React, { useContext, useState, useEffect, useRef } from 'react';
import MaterialTable, {
  MaterialTableProps,
  MTableToolbar,
  MTableBodyRow,
  MTableCell,
  MTableHeader,
  Localization,
} from '@material-table/core';
import { options as defaultOptions } from './options';
import { useIntl } from 'react-intl';
import { Paper } from '@material-ui/core';
import { useStyles } from './MTable.styles';
import { UserContext } from '../../Context/UserContext/UserContext';

export const MTable = <RowData extends object>(
  props: MaterialTableProps<RowData> & { actionLabel?: string; hideToolbar?: boolean },
) => {
  const { tableRowCount } = useContext(UserContext);

  const classes = useStyles();
  const intl = useIntl();
  const { options, components: propsComponents, columns, data, actions, tableRef, ...rest } = props as any;
  const [pageSize, setPageSize] = useState<number>(tableRowCount);
  const [tableKey, setTableKey] = useState<number>(0);

  useEffect(() => {
    if (tableRowCount && tableRowCount !== pageSize) {
      setPageSize(tableRowCount);
      setTableKey(prev => prev + 1);
    }
  }, [tableRowCount]);

  const safeColumns = columns || [];
  const safeData = data || [];
  const safeActions = actions || [];

  const columnHiddenState = useRef<Record<string, boolean>>({});
  const managedColumnsRef = useRef(safeColumns);

  useEffect(() => {
    const updatedColumns = safeColumns.map((col: any) => {
      const field = col.field || col.title;
      if (field && columnHiddenState.current[field] !== undefined) {
        return { ...col, hidden: columnHiddenState.current[field] };
      }
      return col;
    });
    managedColumnsRef.current = updatedColumns;
  }, [safeColumns]);

  const handleColumnHidden = (column: any, hidden: boolean) => {
    const field = column.field || column.title;
    if (field) {
      columnHiddenState.current[field] = hidden;
      // Update ref for next render
      managedColumnsRef.current = managedColumnsRef.current.map((col: any) =>
        (col.field || col.title) === field ? { ...col, hidden } : col
      );
    }
  };

  if ((rest as any).onRowsPerPageChange === undefined) {
    (rest as any).onRowsPerPageChange = setPageSize;
  }

  const defaultComponents = {
    Toolbar: (props: any) => <MTableToolbar {...props} classes={{ root: classes.toolbar }} />,
    Row: (props: any) => <MTableBodyRow {...props} classes={{ root: classes.row }} />,
    Cell: (props: any) => <MTableCell {...props} classes={{ root: classes.cell }} />,
    Header: (props: any) => <MTableHeader {...props} classes={{ header: classes.headerCell }} />,
    Container: (props: any) => <Paper variant="outlined" {...props} className={classes.tableContainer} />,
  };

  const mergedComponents = {
    ...defaultComponents,
    ...(propsComponents || {}),
  };

  return (
    <MaterialTable
      key={tableKey}
      {...rest}
      tableRef={tableRef}
      columns={managedColumnsRef.current}
      data={safeData}
      actions={safeActions}
      onChangeColumnHidden={handleColumnHidden}
      localization={
        {
          pagination: {
            firstTooltip: intl.formatMessage({ id: 'component.mtable.firstTooltip' }),
            labelDisplayedRows: intl.formatMessage({ id: 'component.mtable.labelDisplayedRows' }),
            labelRowsPerPage: intl.formatMessage({ id: 'component.mtable.labelRowsPerPage' }),
            labelRowsSelect: intl.formatMessage({ id: 'component.mtable.labelRowsSelect' }),
            lastTooltip: intl.formatMessage({ id: 'component.mtable.lastTooltip' }),
            nextTooltip: intl.formatMessage({ id: 'component.mtable.nextTooltip' }),
            previousTooltip: intl.formatMessage({ id: 'component.mtable.previousTooltip' }),
          },
          body: {
            addTooltip: intl.formatMessage({ id: 'component.mtable.addTooltip' }),
            deleteTooltip: intl.formatMessage({ id: 'component.mtable.deleteTooltip' }),
            editTooltip: intl.formatMessage({ id: 'component.mtable.editTooltip' }),
            dateTimePickerLocalization: {
              format: 'dd/MM/yyyy',
              monthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
              dayOfWeekNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
            },
          },
          grouping: {
            groupedBy: intl.formatMessage({ id: 'component.mtable.groupedBy' }),
            placeholder: intl.formatMessage({ id: 'component.mtable.placeholder' }),
          },
          header: {
            actions:
              props.actionLabel !== undefined
                ? props.actionLabel
                : intl.formatMessage({ id: 'component.mtable.actions' }),
          },
          toolbar: {
            addRemoveColumns: intl.formatMessage({ id: 'component.mtable.addRemoveColumns' }),
            exportName: intl.formatMessage({ id: 'component.mtable.exportName' }),
            exportTitle: intl.formatMessage({ id: 'component.mtable.exportTitle' }),
            nRowsSelected: intl.formatMessage({ id: 'component.mtable.nRowsSelected' }),
            searchPlaceholder: intl.formatMessage({ id: 'component.mtable.searchPlaceholder' }),
            searchTooltip: intl.formatMessage({ id: 'component.mtable.searchTooltip' }),
            showColumnsTitle: intl.formatMessage({ id: 'component.mtable.showColumnsTitle' }),
            filter: 'Filter',
            clearFilter: 'Clear filter',
          },
        } as Localization
      }
      components={mergedComponents}
      options={{  ...defaultOptions, toolbar: !props.hideToolbar, ...{ pageSize },...options}}
    />
  );
};
export default MTable;
